import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { SlidePanel } from "@/components/SlidePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Sparkles, Search, Wand2, Lock, BookOpen, Check, CheckSquare } from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import EntityContextMenu from "@/components/EntityContextMenu";
import { RelatedEntitiesPanel } from "@/components/RelatedEntitiesPanel";
import { FolderColorStrip } from "@/components/FolderColorStrip";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { useMultiSelect, BulkActionBar, SelectCheckbox } from "@/components/MultiSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALIGNMENTS = [
  "Lawful Good", "Neutral Good", "Chaotic Good",
  "Lawful Neutral", "True Neutral", "Chaotic Neutral",
  "Lawful Evil", "Neutral Evil", "Chaotic Evil"
];

const DeitiesPage = () => {
  const { campaignId } = useParams();
  const { user } = useAuth();
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const [campaign, setCampaign] = useState(null);
  const [deities, setDeities] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createMode, setCreateMode] = useState("manual");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAlign, setFilterAlign] = useState("all");
  const [selectedDeity, setSelectedDeity] = useState(null);
  const [activeTab, setActiveTab] = useState("pantheon");
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "deities", onRefresh: () => fetchData() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const multi = useMultiSelect();

  // Pantheon Library (canonical deities)
  const [libraryDeities, setLibraryDeities] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryFilterAlign, setLibraryFilterAlign] = useState("all");
  const [addingDeity, setAddingDeity] = useState(null);

  const [formData, setFormData] = useState({
    name: "", description: "", domain: "", alignment: "", symbol: "", folder_id: ""
  });

  useEffect(() => { fetchData(); }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, deitiesRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/deities`, { withCredentials: true })
      ]);
      setCampaign(campaignRes.data);
      setDeities(deitiesRes.data);
      folderCrud.refresh();
      if (selectedDeity) {
        const updated = deitiesRes.data.find(d => d.id === selectedDeity.id);
        setSelectedDeity(updated || null);
      }
    } catch { toast.error("Failed to load deities"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, campaign_id: campaignId, folder_id: formData.folder_id || null };
      if (editing) {
        await axios.put(`${API}/deities/${editing.id}`, payload, { withCredentials: true });
        toast.success("Deity updated");
      } else {
        await axios.post(`${API}/deities`, payload, { withCredentials: true });
        toast.success("Deity created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch { toast.error("Failed to save deity"); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Deity", "Are you sure you want to delete this deity?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/deities/${id}`, { withCredentials: true });
      toast.success("Deity deleted");
      if (selectedDeity?.id === id) setSelectedDeity(null);
      fetchData();
    } catch { toast.error("Failed to delete deity"); }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ name: "", description: "", domain: "", alignment: "", symbol: "", folder_id: folderCrud.currentFolderId || "" });
  };

  const openEdit = (deity) => {
    setEditing(deity);
    setFormData({
      name: deity.name, description: deity.description || "",
      domain: deity.domain || "", alignment: deity.alignment || "",
      symbol: deity.symbol || "", folder_id: deity.folder_id || ""
    });
    setSelectedDeity(null);
    setDialogOpen(true);
  };

  const openCreate = () => { resetForm(); setCreateMode("manual"); setDialogOpen(true); };

  const filtered = deities.filter(d => {
    const matchSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchAlign = filterAlign === "all" || d.alignment === filterAlign;
    return matchSearch && matchAlign;
  });
  const { folders: levelFolders, items: filteredByFolder } = partitionCurrentLevel({
    folders, items: filtered, currentFolderId: folderCrud.currentFolderId
  });
  const searching = searchTerm.trim() !== "" || filterAlign !== "all";
  const displayFolders = searching ? [] : levelFolders;
  const displayItems = searching ? filtered : filteredByFolder;

  const bulkMove = async (folderId) => {
    const ids = [...multi.selectedIds];
    try {
      await Promise.all(ids.map(id => axios.put(`${API}/campaigns/${campaignId}/deities/${id}/folder`, { folder_id: folderId }, { withCredentials: true })));
      setDeities(prev => prev.map(d => ids.includes(d.id) ? { ...d, folder_id: folderId } : d));
      multi.clearAll();
      toast.success(`Moved ${ids.length} deities`);
    } catch { toast.error("Failed to move"); }
  };

  const bulkDelete = async () => {
    const ids = [...multi.selectedIds];
    if (!window.confirm(`Delete ${ids.length} deities?`)) return;
    try {
      await Promise.all(ids.map(id => axios.delete(`${API}/deities/${id}`, { withCredentials: true })));
      setDeities(prev => prev.filter(d => !ids.includes(d.id)));
      multi.clearAll();
      toast.success(`Deleted ${ids.length} deities`);
    } catch { toast.error("Failed to delete"); }
  };

  // Pantheon Library
  const addedLibraryNames = new Set(deities.map(d => d.name.toLowerCase()));
  const fetchLibrary = async () => {
    if (libraryDeities.length > 0) return;
    setLibraryLoading(true);
    try {
      const res = await axios.get(`${API}/srd-deities`, { withCredentials: true });
      setLibraryDeities(res.data);
    } catch { toast.error("Failed to load Pantheon Library"); }
    finally { setLibraryLoading(false); }
  };
  const addLibraryDeity = async (lib) => {
    setAddingDeity(lib.id);
    try {
      await axios.post(`${API}/srd-deities/add-to-campaign`, { campaign_id: campaignId, srd_id: lib.id }, { withCredentials: true });
      toast.success(`${lib.name} added to your pantheon`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add deity"); }
    finally { setAddingDeity(null); }
  };
  const filteredLibrary = libraryDeities.filter(d => {
    const matchSearch = d.name.toLowerCase().includes(librarySearch.toLowerCase()) ||
                        (d.domain || "").toLowerCase().includes(librarySearch.toLowerCase());
    const matchAlign = libraryFilterAlign === "all" || d.alignment === libraryFilterAlign;
    return matchSearch && matchAlign;
  });

  if (loading) {
    return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="deities-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Deities</h1>
            <p className="text-muted-foreground text-sm">Document the pantheon and divine beings</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={multi.selectMode ? "default" : "outline"} className="rounded-sm text-xs" onClick={() => multi.setSelectMode(!multi.selectMode)} data-testid="toggle-select-mode">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> {multi.selectMode ? "Cancel" : "Select"}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} data-testid="create-deity-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                  <Plus className="w-4 h-4 mr-2" /> New Deity
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">{editing ? "Edit Deity" : "New Deity"}</DialogTitle>
                </DialogHeader>
                {!editing && (
                  <div className="flex gap-2 border-b border-border pb-3 mb-1">
                    <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                      <Plus className="w-3 h-3 mr-1" /> Create Manually
                    </Button>
                    {!isCommoner && (
                      <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                        <Wand2 className="w-3 h-3 mr-1" /> Use AI
                      </Button>
                    )}
                    {isCommoner && (
                      <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                        <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                      </Button>
                    )}
                  </div>
                )}
                {!editing && createMode === "ai" && (
                  <AICreateMode entityType="deity" campaignId={campaignId} onGenerated={(data) => {
                    setFormData(prev => ({ ...prev, ...data }));
                    setCreateMode("manual");
                  }} />
                )}
                <form onSubmit={handleSubmit} className="space-y-4 mt-4" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Name</label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Pelor, the Sun God" required data-testid="deity-name-input" className="bg-input border-border rounded-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Domain</label>
                      <Input value={formData.domain} onChange={(e) => setFormData({ ...formData, domain: e.target.value })} placeholder="Light, Healing, Life" data-testid="deity-domain-input" className="bg-input border-border rounded-sm" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Alignment</label>
                      <Select value={formData.alignment} onValueChange={(v) => setFormData({ ...formData, alignment: v })}>
                        <SelectTrigger className="bg-input border-border rounded-sm" data-testid="deity-alignment-select"><SelectValue placeholder="Select alignment" /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {ALIGNMENTS.map(align => <SelectItem key={align} value={align}>{align}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Symbol</label>
                      <Input value={formData.symbol} onChange={(e) => setFormData({ ...formData, symbol: e.target.value })} placeholder="A golden sun with rays" data-testid="deity-symbol-input" className="bg-input border-border rounded-sm" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Folder</label>
                      <Select value={formData.folder_id || "none"} onValueChange={v => setFormData({ ...formData, folder_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="bg-input border-border rounded-sm" data-testid="deity-folder-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          <SelectItem value="none">Unfiled</SelectItem>
                          {folders.filter(f => !f.archived).sort((a, b) => a.name.localeCompare(b.name)).map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Description</label>
                    <RichTextEditor value={formData.description} onChange={(value) => setFormData({ ...formData, description: value })} placeholder="Describe the deity, their history, worship, temples..." />
                  </div>
                  <Button type="submit" data-testid="save-deity-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                    {editing ? "Save Changes" : "Create Deity"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "library") fetchLibrary(); }} className="w-full">
          <TabsList className="bg-muted mb-6">
            <TabsTrigger value="pantheon" data-testid="tab-my-pantheon">My Pantheon</TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-pantheon-library">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Pantheon Library
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pantheon">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search deities..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="deity-search" />
              </div>
              <Select value={filterAlign} onValueChange={setFilterAlign}>
                <SelectTrigger className="w-48" data-testid="deity-filter-alignment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Alignments</SelectItem>
                  {ALIGNMENTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Folder browser */}
            <div className="flex gap-4 items-start">
              <FolderRail folders={folders} items={deities} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} allLabel="All Deities" className="w-44 shrink-0 sticky top-2" testIdPrefix="deities" />
              <div className="flex-1 min-w-0">
                <FolderToolbar folders={folders} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} onNewFolder={folderCrud.createHere} allLabel="All Deities" className="mb-3" />
                {displayFolders.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="deities-folder-grid">
                    {displayFolders.map(f => (
                      <FolderCard key={f.id} folder={f}
                        count={deities.filter(d => d.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                        onOpen={() => folderCrud.setCurrentFolderId(f.id)} onRename={() => folderCrud.renameFolder(f)} onDelete={() => folderCrud.deleteFolder(f)} onArchive={() => folderCrud.toggleArchive(f)} onColor={(c) => folderCrud.setFolderColor(f, c)} onDropItem={folderCrud.moveItem} />
                    ))}
                  </div>
                )}

                {displayItems.length === 0 && displayFolders.length === 0 ? (
                  <div className="empty-state">
                    <Sparkles className="empty-state-icon text-muted-foreground w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-serif text-foreground mb-2">{deities.length === 0 ? "No deities yet" : "No matches"}</h3>
                    <p className="text-muted-foreground mb-6">{deities.length === 0 ? "Add gods and divine beings to your pantheon" : "Try a different search"}</p>
                    {deities.length === 0 && <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Create Deity</Button>}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {displayItems.map((deity) => (
                      <EntityContextMenu key={deity.id} onOpen={() => setSelectedDeity(deity)} onRename={() => openEdit(deity)} onDelete={() => handleDelete(deity.id)}
                        folders={folders} currentFolderId={deity.folder_id} onMoveFolder={(fid) => folderCrud.moveItem(deity.id, fid)} pinPath="deities" pinLabel={`Deity: ${deity.name}`} entityName={deity.name}>
                        <Card
                          className={`bg-card border-border rounded-sm cursor-pointer entity-card relative transition-colors hover:border-primary/40 ${multi.isSelected(deity.id) ? "ring-2 ring-primary" : ""}`}
                          onClick={() => multi.selectMode ? multi.toggle(deity.id) : setSelectedDeity(deity)}
                          draggable onDragStart={e => e.dataTransfer.setData("entityId", deity.id)}
                          data-testid={`deity-card-${deity.id}`}
                        >
                          <FolderColorStrip entityId={deity.id} folderId={deity.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="deity" />
                          {multi.selectMode && <SelectCheckbox checked={multi.isSelected(deity.id)} onChange={() => multi.toggle(deity.id)} />}
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-primary" /></div>
                                <div>
                                  <h3 className="font-serif text-lg text-foreground">{deity.name}</h3>
                                  <p className="text-xs text-muted-foreground">{deity.domain || "Unknown domain"}</p>
                                </div>
                              </div>
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <VisibilityToggle entityType="deities" entityId={deity.id} currentVisibility={deity.visibility || "dm_only"} onUpdate={(v) => setDeities(prev => prev.map(d => d.id === deity.id ? { ...d, visibility: v } : d))} />
                              </div>
                            </div>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              {deity.alignment && <p>Alignment: {deity.alignment}</p>}
                              {deity.symbol && <p>Symbol: {deity.symbol}</p>}
                            </div>
                          </CardContent>
                        </Card>
                      </EntityContextMenu>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="library">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={librarySearch} onChange={(e) => setLibrarySearch(e.target.value)} placeholder="Search name or domain... (e.g. Bane, Melora, war, nature)" className="pl-10 bg-input border-border rounded-sm" data-testid="library-search-deities" />
              </div>
              <Select value={libraryFilterAlign} onValueChange={setLibraryFilterAlign}>
                <SelectTrigger className="w-[180px] bg-input border-border rounded-sm" data-testid="library-filter-alignment"><SelectValue placeholder="All Alignments" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All Alignments</SelectItem>
                  {ALIGNMENTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {libraryLoading ? (
              <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-4" data-testid="library-deities-count">
                  {filteredLibrary.length} of {libraryDeities.length} deities &mdash; Click &quot;Add&quot; to copy to your pantheon
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredLibrary.map(lib => {
                    const isAdded = addedLibraryNames.has(lib.name.toLowerCase());
                    const isAdding = addingDeity === lib.id;
                    return (
                      <Card key={lib.id} className="bg-card border-border rounded-sm" data-testid={`library-deity-${lib.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5 text-primary" /></div>
                              <div className="min-w-0">
                                <h4 className="font-serif text-base text-foreground truncate">{lib.name}</h4>
                                <p className="text-xs text-muted-foreground truncate">{lib.rank}</p>
                              </div>
                            </div>
                            <Button size="sm" variant={isAdded ? "outline" : "default"} className="rounded-sm text-xs shrink-0" disabled={isAdded || isAdding} onClick={() => addLibraryDeity(lib)} data-testid={`add-library-deity-${lib.id}`}>
                              {isAdded ? (<><Check className="w-3 h-3 mr-1" /> Added</>) : isAdding ? "Adding..." : (<><Plus className="w-3 h-3 mr-1" /> Add</>)}
                            </Button>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-0.5 mb-2">
                            <p><span className="font-medium text-foreground/80">Alignment:</span> {lib.alignment}</p>
                            <p><span className="font-medium text-foreground/80">Domain:</span> {lib.domain}</p>
                            <p><span className="font-medium text-foreground/80">Symbol:</span> {lib.symbol}</p>
                          </div>
                          <p className="text-xs text-foreground/70 line-clamp-3">{lib.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                {filteredLibrary.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground text-sm">No deities match your search.</div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Deity Detail SlidePanel */}
        <SlidePanel open={!!selectedDeity} onClose={() => setSelectedDeity(null)} title={selectedDeity?.name || "Deity"}
          items={filtered} currentIndex={selectedDeity ? filtered.findIndex(d => d.id === selectedDeity.id) : -1}
          onNavigate={(i) => setSelectedDeity(filtered[i])}
        >
          {selectedDeity && (
            <div data-testid="deity-detail-panel">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center"><Sparkles className="w-6 h-6 text-primary" /></div>
              </div>
              <div className="space-y-3 mb-4">
                {selectedDeity.domain && (<div><h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Domain</h4><p className="text-sm text-foreground">{selectedDeity.domain}</p></div>)}
                {selectedDeity.alignment && (<div><h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Alignment</h4><p className="text-sm text-foreground">{selectedDeity.alignment}</p></div>)}
                {selectedDeity.symbol && (<div><h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Symbol</h4><p className="text-sm text-foreground">{selectedDeity.symbol}</p></div>)}
              </div>
              {selectedDeity.description && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Description</h4>
                  <div className="text-sm text-foreground/80 prose-sm"><RichTextDisplay content={selectedDeity.description} /></div>
                </div>
              )}
              <div className="pt-4 border-t border-border mb-4">
                <RelatedEntitiesPanel entityType="deities" entityId={selectedDeity.id} />
              </div>
              <div className="flex gap-2 pt-4 border-t border-border">
                <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => { setSelectedDeity(null); openEdit(selectedDeity); }} data-testid="deity-detail-edit">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedDeity.id)} data-testid="deity-detail-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </SlidePanel>
      </div>
      <BulkActionBar count={multi.count} folders={folders} onBulkMove={bulkMove} onBulkDelete={bulkDelete} onClear={multi.clearAll} />
    </Layout>
  );
};

export default DeitiesPage;
